import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Offcanvas } from 'reactstrap';
import { observer } from 'mobx-react-lite';
import environment from '@environment';
import { IconsButton } from 'components/buttons/icons-button/icons-button';
import { WaitingLoader } from 'components/loaders/waiting-loader/waiting-loader';
import { Panel } from 'components/panels/panel';
import { PanelThemeType, PanelType } from 'components/panels/panel-type';
import { AppContext } from 'core/context/app.context';
import { CsatActions, GenexActions } from 'core/enums/genex';
import { usePlatform } from 'core/hooks/platform';
import { LiveChatService } from 'core/services/api/live-chat';
import { GenexStore, ModelWithCategory } from 'core/stores/genex.store';
import { SiteConfigStore } from 'core/stores/site-config.store';
import { Util } from 'utils/common.utils';
import { LiveChatStore } from './live-chat.store';
import styles from './live-chat.module.scss';

export const LiveChatIFrame = observer(({ shouldOpen, store }: { shouldOpen?: boolean; store: LiveChatStore }) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation();
  const { genexActionCode } = usePlatform();

  useEffect(() => {
    store.initialize(appContext.customerNumber, appContext.countryName, shouldOpen);

    addCloseEventListener();
  }, [t, appContext, store]);

  const initiateChat = async () => {
    if (store.isChatMinimised) {
      store.changeIsChatMinimised(false);
      return;
    }

    store.changeIsChatOpen(true);
    addCloseEventListener();
  };

  useEffect(() => {
    if(shouldOpen) {
      initiateChat();
    }
  }, [shouldOpen]);

  const handleMessageEvent = (event: MessageEvent<any>) => {
    if (event.origin !== environment.LIVE_CHAT_IFRAME) return;

    const data = Util.TryParseJSON(event?.data);

    if (data === 'closeIFrame' || (data?.event === 'closeIFrame' && data?.isOOORunning === false)) {
      handleCloseWithSurvey();
      return;
    }

    if (data?.event === 'minimizeIFrame' || data === 'minimizeIFrame') {
      handleMinimize();
      return;
    }

    handleClose();
  };

  const handleClose = () => {
    store.disconnectChat();
    window.removeEventListener('message', handleMessageEvent);
  };

  const handleMinimize = () => {
    store.changeIsChatMinimised(true);
  };

  const handleCloseWithSurvey = () => {
    handleClose();

    const surveyModel = {
      category: GenexActions.LiveChat,
      outcome: 'Success',
      surveyName: genexActionCode(CsatActions.LiveChat),
    } as ModelWithCategory;

    GenexStore.saveUserData({
      ...GenexStore.optionalUserData,
      smartCardNumber: store.smartcardNumber || '',
    });
    GenexStore.triggerSurvey(surveyModel, appContext.customerNumber, new Date());
  };

  const addCloseEventListener = () => {
    window.addEventListener('message', handleMessageEvent, false);
  };

  const getDisplayName = () => {
    return (
      <>
        <svg width="16" height="16" viewBox="0 0 16 16">
          <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
            <path
              d="M16,2 
              L16,10.4444444 
              C16,11.5490139 15.1045695,12.4444444 
              14,12.4444444 L11.9109137,12.4444444 
              L11.9109137,16 L7.74039,12.4444444 
              L2,12.4444444 C0.8954305,12.4444444 
              0,11.5490139 0,10.4444444 
              L0,2 C0,0.8954305 0.8954305,0 2,0 L14,0 C15.1045695,0 16,0.8954305 16,2 Z"
              id="chat"
              fill="#FFFFFF"
            ></path>
          </g>
        </svg>
        <span className={`cust-ml-1`}>{t('liveChat.buttonTitle')}</span>
      </>
    );
  };

  return (
    <Panel backgroundType={PanelThemeType.Transparent} sizeType={PanelType.FullWidth}>
      <div className={styles.liveChat}>
        {(!store.isChatOpen || store.isChatMinimised) && !store.isLoading && store.subscription?.smartcardNumber && (
          <IconsButton
            meta={{
              name: t('liveChat.buttonTitle'),
              title: t('liveChat.buttonTitle'),
            }}
            classes={['cust-ml-1', 'btn', styles.liveChatButton]}
            onClick={initiateChat}
          >
            {getDisplayName()}
          </IconsButton>
        )}
        {store.isChatOpen && (
          <Offcanvas
            direction="bottom"
            isOpen={store.isChatOpen && !store.isChatMinimised}
            unmountOnClose={false}
            className={`${styles.iFrameCanvasContainer}`}
          >
            <WaitingLoader
              titles={[t('liveChat.loaderTitle', { name: store?.user?.firstName })]}
              texts={[t('liveChat.loaderDescription')]}
              isLoading={store.isLoading}
            >
              <div className={`d-flex flex-column h-100 ${styles.liveChat}`}>
                <iframe
                  className="h-100"
                  src={LiveChatService.IFrameContentURL(
                    `${store.user?.firstName} ${store.user?.surname}`,
                    store.user?.firstName,
                    store.user?.surname,
                    SiteConfigStore.businessUnit(),
                    store.smartcardNumber,
                    appContext.countryName,
                    appContext.customerNumber,
                    appContext.lang
                  )}
                />
                {/* <div className={`d-flex justify-content-end ${styles.liveChatIFrameButtonWrapper}`}>
                <IconsButton
                  meta={{
                    name: t('buttons.close'),
                    title: t('buttons.close'),
                  }}
                  classes={['cust-ml-1', 'btn', styles.liveChatIFrameButton]}
                  onClick={handleClose}
                >
                  {t('buttons.close')}
                </IconsButton>
              </div> */}
              </div>
            </WaitingLoader>
          </Offcanvas>
        )}
      </div>
    </Panel>
  );
});